import { Accessibility, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { SITE_CONFIG } from "@/config/site";

const LAST_UPDATED = "15 May 2026";

const SectionHeading = ({ children, id }) => (
  <h2 id={id} className="text-2xl md:text-3xl font-bold text-white mt-12 mb-4">{children}</h2>
);

const BulletList = ({ items }) => (
  <ul className="text-gray-300 text-sm space-y-2 mb-4 list-disc list-inside ml-1">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export const AccessibilityPage = () => {
  return (
    <div className="pt-32 pb-20 min-h-screen inner-page bg-black-v2" data-testid="accessibility-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          {/* <Badge className="section-badge mb-6">
            <Accessibility className="w-3 h-3 text-violet-400" />
            <span>Accessibility</span>
          </Badge> */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 mt-20">
            Accessibility Statement
          </h1>
          <p className="text-gray-400 text-sm">Last Updated: {LAST_UPDATED}</p>
        </div>

        <SectionHeading>Our Commitment</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          At QVPN, we are committed to ensuring digital accessibility for all users, including
          people with disabilities. We believe that everyone deserves equal access to our website,
          applications, and services, regardless of ability.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          We are continually working to improve the accessibility and usability of our digital
          properties to ensure we provide a positive experience for all visitors.
        </p>

        <SectionHeading>Accessibility Standards</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
          standards. These guidelines explain how to make web content more accessible to people
          with a wide range of disabilities, including:
        </p>
        <BulletList
          items={[
            "Visual impairments (blindness, low vision, color blindness)",
            "Hearing impairments",
            "Motor impairments",
            "Cognitive disabilities"
          ]}
        />

        <SectionHeading>Measures We Have Taken</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          To ensure accessibility, QVPN has implemented the following measures:
        </p>
        <ul className="text-gray-300 text-sm space-y-3 mb-4">
          <li>
            <span className="font-semibold text-white">Semantic HTML:</span>{" "}
            We use proper heading structures, landmarks, and labels to assist screen reader users.
          </li>
          <li>
            <span className="font-semibold text-white">Keyboard Navigation:</span>{" "}
            All interactive elements are accessible via keyboard.
          </li>
          <li>
            <span className="font-semibold text-white">Color Contrast:</span>{" "}
            We maintain sufficient color contrast ratios for text and interactive elements.
          </li>
          <li>
            <span className="font-semibold text-white">Alternative Text:</span>{" "}
            Images include descriptive alt text where appropriate.
          </li>
          <li>
            <span className="font-semibold text-white">Focus Indicators:</span>{" "}
            Visible focus states are provided for all interactive elements.
          </li>
          <li>
            <span className="font-semibold text-white">Responsive Design:</span>{" "}
            Our website and applications work across various devices and screen sizes.
          </li>
          <li>
            <span className="font-semibold text-white">Form Accessibility:</span>{" "}
            Form fields include clear labels and error messages.
          </li>
          <li>
            <span className="font-semibold text-white">Skip Links:</span>{" "}
            Skip navigation links are provided for screen reader users.
          </li>
        </ul>

        <SectionHeading>Ongoing Efforts</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          Accessibility is an ongoing process. We are committed to:
        </p>
        <BulletList
          items={[
            "Regular accessibility audits of our website and applications",
            "Training our team on accessibility best practices",
            "Incorporating accessibility considerations into our design and development processes",
            "Testing with assistive technologies including screen readers",
            "Addressing accessibility issues promptly when identified"
          ]}
        />

        <SectionHeading>Compatibility</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          Our website and applications are designed to be compatible with:
        </p>

        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Browsers</h3>
        <BulletList
          items={[
            "Google Chrome (latest version)",
            "Mozilla Firefox (latest version)",
            "Apple Safari (latest version)",
            "Microsoft Edge (latest version)"
          ]}
        />

        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Assistive Technologies</h3>
        <BulletList
          items={[
            "Screen readers (NVDA, JAWS, VoiceOver)",
            "Screen magnification software",
            "Speech recognition software",
            "Keyboard-only navigation"
          ]}
        />

        <SectionHeading>Known Limitations</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          While we strive for full accessibility, some areas may have limitations:
        </p>
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden mb-4">
          <div className="grid grid-cols-3 border-b border-white/10">
            <div className="p-4 text-sm font-medium text-gray-300">Content</div>
            <div className="p-4 text-sm font-medium text-gray-300">Issue</div>
            <div className="p-4 text-sm font-medium text-gray-300">Status</div>
          </div>
          {[
            {
              content: "Third party widgets",
              issue: "Some embedded content from third parties may not be fully accessible",
              status: "Working with providers"
            },
            {
              content: "PDF documents",
              issue: "Older PDF documents may lack full accessibility features",
              status: "Being remediated"
            },
            {
              content: "Live chat",
              issue: "Some chat features may have limited screen reader support",
              status: "Under review"
            }
          ].map((row, i, arr) => (
            <div
              key={i}
              className={`grid grid-cols-3 ${i !== arr.length - 1 ? "border-b border-white/5" : ""}`}
            >
              <div className="p-4 text-sm text-gray-300">{row.content}</div>
              <div className="p-4 text-sm text-gray-400">{row.issue}</div>
              <div className="p-4 text-sm qvpn-ft">{row.status}</div>
            </div>
          ))}
        </div>
        <p className="text-gray-400 text-sm italic">
          We are actively working to address these limitations and improve accessibility across
          all our digital properties.
        </p>

        <SectionHeading>Feedback and Assistance</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          We welcome your feedback on the accessibility of QVPN. If you encounter any
          accessibility barriers or have suggestions for improvement, please contact us:
        </p>
        <a
          href={`mailto:${SITE_CONFIG.contact.supportEmail}`}
          className="inline-flex items-center gap-2 link mb-4"
        >
          <Mail className="w-4 h-4" />
          {SITE_CONFIG.contact.supportEmail}
        </a>
        <p className="text-gray-400 text-sm">
          <span className="font-medium text-gray-300">Response Time:</span>{" "}
          We aim to respond to accessibility feedback within 5 business days.
        </p>

        <SectionHeading>Alternative Access</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          If you are unable to access any content or functionality on our website or applications
          due to a disability, please contact us. We will work with you to provide the information
          or service you need through an alternative method.
        </p>

        <SectionHeading>Formal Complaints</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          If you are not satisfied with our response to your accessibility concern, you may
          escalate your complaint by contacting:
        </p>
        <a
          href="mailto:support@qvpn.io"
          className="inline-flex items-center gap-2 link"
        >
          <Mail className="w-4 h-4" />
          support@qvpn.io
        </a>

        <SectionHeading>Continuous Improvement</SectionHeading>
        <p className="text-gray-300 leading-relaxed mb-4">
          This accessibility statement was last reviewed on {LAST_UPDATED}. We review and update
          this statement regularly to reflect our ongoing accessibility efforts.
        </p>
      </div>
    </div>
  );
};

export default AccessibilityPage;
